// src/components/ActivityFeed.jsx
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { ArrowUpCircle, ArrowDownCircle, Info } from 'lucide-react';
import TypingIndicator from "./TypingIndicator";
import HumanTyping from "./ui/HumanTyping";
import { useScrollZones } from "../hooks/useScrollZones";

export default function ActivityFeed({ isAwaitingResponse, type, activity, enableChat, onSend }) {
  const containerRef                      = useRef(null);
  const spacerRef                         = useRef(null);
  const introRef                          = useRef(null);
  const itemRefs                          = useRef({});
  const [readableType, setReadableType]   = useState('')
  const [firstLoad, setFirstLoad]         = useState(activity.messages.length >= 1 || !isAwaitingResponse)
  const zone                              = useScrollZones(containerRef);
  const [textDisplayed, setTextDisplayed] = useState([])
  const [selectedButton, setSelectedButton] = useState(null);

  function sentenceDone(messageId) {
    enableChat()
    scrollToNeeded(false)
    setTextDisplayed((prev) => [...prev, messageId]);
    setFirstLoad(false)
  }
  function scrollToNeeded() {
    const lastUser   = [...activity.messages].reverse().find((m) => m.sender === "user");
    const lastBot   = [...activity.messages].reverse().find((m) => m.sender !== "user");
    const lastMessage   = activity.messages[activity.messages.length - 1];
    const penultimateMessage   = activity.messages[activity.messages.length - 2];

    if ( !lastUser || !lastBot ) return;

    const userRef = itemRefs.current[lastUser.id].el
    const botRef = itemRefs.current[lastBot.id].el
    const penultimateRef = itemRefs.current[penultimateMessage.id].el
    let available = containerRef.current.clientHeight - userRef.offsetHeight;

    if ( botRef && lastMessage.sender !== "user")
      available = available - botRef.offsetHeight - 48

    if ( penultimateRef && penultimateMessage.sender !== "user" && lastMessage.sender !== "user")
      available = available - penultimateRef.offsetHeight - 24

    if (!containerRef.current || !userRef) return;

    spacerRef.current.style.minHeight = `${Math.max(0, available)}px`;

    botRef.scrollIntoView({
      behavior: "smooth",
      block: "start",
      inline: "nearest"
    });
  }

  useEffect(() => {
    if ( type == 'grit-and-fit')
      setReadableType("🎮 Dans la peau d'un pro !")
    else
      setReadableType("🎯 Quizz !")
  }, [])

  useLayoutEffect(() => {
    if ( firstLoad ) {
      setTimeout(() => {
        scrollToNeeded()
      }, 400)
    } else {
      scrollToNeeded()
    }
  }, [activity]);

  const scrollTop = () => {
    introRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  const scrollBottom = () => {
    const lastUser   = [...activity.messages].reverse().find((m) => m.sender === "user");
    const targetNode = itemRefs.current[lastUser.id].el;
    targetNode.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <div
      ref={containerRef}
      className="flex-1 relative overflow-y-auto no-scrollbar
      max-w-3xl w-full mx-auto d-vh mb-4 px-6
      flex flex-col gap-6 max-h-screen"
    >
      <div className="flex items-center flex-col" ref={introRef}>
        <img
              src={activity.photo_url}
              alt={activity.title}
              className="w-16 h-16 rounded-full object-cover mb-4"
            />
        <h1 className="text-2xl font-semibold mb-1">{activity.title} {readableType}</h1>
      </div>
      {activity.messages.map((m, i) => {
        const prevMsg = activity.messages[i + 1];
        return (<div
            key={m.id}
            ref={(el) => (itemRefs.current[m.id] = { el: el, sender: m.sender })}
            className={
              "whitespace-pre-line break-words leading-relaxed " +
              (m.sender === "user"
                ? "self-end bg-gray-800 px-6 py-3 rounded-2xl max-w-[80%] text-left"
                : "self-start text-base text-left") +
                (m.quizzResponse
                  ? " hidden"
                  : "")
            }
          >
          { m.type == "text" && !m.quizzResponse && (m.sender === "user" ? m.content : <HumanTyping firstLoad={firstLoad} text={m.content} sentenceDone={sentenceDone} messageId={m.id} />)}
          { m.type == "button" && textDisplayed.includes(m.referrer_message_id) && (
            <div className="flex flex-wrap gap-4 w-full mb-6">
            {m.content.map((button, idx) => {
              const isSelected = selectedButton === button.name || prevMsg?.quizzResponse === button.name;
              return (<button
                key={idx}
                disabled={!!prevMsg?.quizzResponse}
                className={"disabled:cursor-not-allowed flex flex-col items-start justify-start whitespace-pre-line rounded-2xl border border-custom-700 px-3 py-3 text-zinc-200 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary text-left " +
                  (isSelected ? "bg-green-700" : " bg-gray-800")}
                onClick={() => {
                  setSelectedButton(button.name);
                  onSend(button.name, button);
                }}
              >
                {button.name}
              </button>
            )})}
          </div>)}
        </div>
      )})}
      {isAwaitingResponse &&
        <div className="message recieved">
            <TypingIndicator />
        </div>}
      <div ref={spacerRef} aria-hidden />
      <div className="fixed left-1/2 bottom-[5px]"
          style={{transform: "translateX(calc(-50% + var(--sidebar-w) / 2))"}}
      >
      {zone === "BOTTOM" && (
        <button onClick={scrollTop}
          className="rounded-full p-2 hover:bg-[#2C3E50]/10"
        >
          <ArrowUpCircle size={25} strokeWidth={1} />
        </button>
      )}
      {zone === "TOP" && (
        <button onClick={scrollBottom}
          className="rounded-full p-2 hover:bg-[#2C3E50]/10"
        >
          <ArrowDownCircle size={25} strokeWidth={1} />
        </button>
      )}
      </div>
    </div>
  );
}
